import { motion } from "framer-motion";
import { Waves, Layers } from "lucide-react";

const RANK = { strong: 0, moderate: 1, tentative: 2 };
export const topObservations = (obs = []) =>
  [...obs].map((o, i) => ({ o, i })).sort((a, b) => (RANK[a.o.strength] ?? 3) - (RANK[b.o.strength] ?? 3) || a.i - b.i)
    .slice(0, 2).map((x) => x.o);

const ease = [0.22, 1, 0.36, 1];

const Layer = ({ label, children }) => (
  <div className="px-6 py-4">
    <p className="eyebrow text-[10px] text-slate-500">{label}</p>
    <p className="mt-1.5 text-[15px] leading-relaxed text-slate-700">{children}</p>
  </div>
);

export const UnsaidObservations = ({ observations }) => (
  <div className="mt-10 space-y-6" data-testid="dual-unsaid-observations">
    {observations.map((o, i) => (
      <motion.article key={i} data-testid={`dual-unsaid-observation-${i}`}
        initial={{ opacity: 0, y: 24, filter: "blur(8px)" }} whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7, delay: i * 0.15, ease }}
        className="fio-glass-strong fio-hairline relative overflow-hidden rounded-[28px]">
        <div className="flex items-center gap-2 px-6 pt-5">
          <span className="eyebrow flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[10px] text-white">{i + 1}</span>
          <span className="eyebrow text-[10px] text-slate-400">Insight {i + 1} of {observations.length}</span>
        </div>
        <Layer label="What you said">{o.what_you_said}</Layer>
        <div className="mx-6 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        <Layer label="What I noticed">{o.what_i_noticed}</Layer>
        <div className="relative mt-1 overflow-hidden bg-gradient-to-br from-teal-50 via-white to-violet-50 px-6 py-6">
          <span aria-hidden className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-violet-300/30 blur-3xl" />
          <p className="eyebrow relative text-[10px] text-violet-700">The Unsaid</p>
          <p className="relative mt-2 font-heading text-[19px] font-semibold leading-snug text-slate-900">{o.the_unsaid}</p>
        </div>
      </motion.article>
    ))}
  </div>
);

const XCard = ({ icon: Icon, kicker, title, children, delay, testId, accent }) => (
  <motion.div data-testid={testId} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.65, delay, ease }}
    className="fio-glass-strong relative overflow-hidden rounded-[28px] p-6">
    <span aria-hidden className="absolute -left-10 -top-10 h-32 w-32 rounded-full blur-3xl" style={{ background: accent, opacity: 0.25 }} />
    <div className="relative flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-2xl text-white" style={{ background: `linear-gradient(135deg, ${accent}, #6D5BD0)` }}>
        <Icon className="h-5 w-5" strokeWidth={2} />
      </span>
      <p className="eyebrow text-[10px] text-slate-500">{kicker}</p>
    </div>
    {title && <h3 className="relative mt-4 text-[22px] font-bold leading-tight tracking-tight text-slate-900">{title}</h3>}
    <p className="relative mt-2 text-[15.5px] leading-relaxed text-slate-700">{children}</p>
  </motion.div>
);

const Connector = () => (
  <div className="relative mx-auto h-12 w-px" aria-hidden>
    <div className="absolute inset-0 bg-gradient-to-b from-teal-400 to-violet-400" />
    <motion.span className="absolute left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.9)]"
      initial={{ top: 0 }} animate={{ top: "100%" }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} />
  </div>
);

export const XraySections = ({ xray }) => {
  if (xray?.crisis) {
    return (
      <div className="mt-10 rounded-[28px] bg-amber-50 p-6 text-left ring-1 ring-amber-200" data-testid="xray-reveal-crisis">
        <p className="text-base leading-relaxed text-slate-800">{xray?.primary_state?.detail}</p>
        <a href="tel:988" data-testid="xray-reveal-crisis-call" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-4 text-base font-semibold text-white hover:bg-slate-800">Call or text 988</a>
        <p className="mt-3 text-center text-sm text-slate-600">US Suicide &amp; Crisis Lifeline · available 24/7</p>
      </div>
    );
  }
  const limited = xray?.confidence === "limited";
  const beneath = !limited && (xray?.underneath?.detail ? xray.underneath : xray?.driver?.detail ? xray.driver : null);
  const themes = (xray?.supporting_themes || []).slice(0, 4);
  return (
    <div className="mt-10">
      <XCard testId="xray-reveal-primary" icon={Waves} kicker="Your emerging emotional pattern" title={xray?.primary_state?.label} accent="#0D9488">
        {xray?.primary_state?.detail}
      </XCard>
      {themes.length > 0 && (
        <div className="mt-3 flex flex-wrap justify-center gap-2" data-testid="xray-themes">
          {themes.map((t) => <span key={t} className="rounded-full bg-white/70 px-3 py-1 text-xs text-slate-600 ring-1 ring-slate-200">{t}</span>)}
        </div>
      )}
      {beneath && (
        <>
          <Connector />
          <XCard testId="xray-reveal-beneath" icon={Layers} kicker="What may be happening beneath the surface" title={beneath.label} accent="#7C3AED" delay={0.1}>
            {beneath.detail}
          </XCard>
        </>
      )}
      <p className="mx-auto mt-8 max-w-sm text-center text-sm leading-relaxed text-slate-500" data-testid="xray-ongoing">
        Understanding emotional patterns is an ongoing process. Think of this as the first chapter — not the whole story.
      </p>
    </div>
  );
};
